import React, { useState } from 'react';
import {
	closestCorners,
	DndContext,
	DragOverlay,
	useDroppable,
} from '@dnd-kit/core';
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	useSortable,
} from '@dnd-kit/sortable';
import { Card } from '@mantine/core';
import { CSS } from '@dnd-kit/utilities';

const Row = ({ rowId, fields }) => {
	const { setNodeRef } = useDroppable({
		id: rowId,
	});
	return (
		<SortableContext
			id={rowId}
			items={fields}
			strategy={horizontalListSortingStrategy}
		>
			<div
				ref={setNodeRef}
				style={{
					display: 'flex',
					flexDirection: 'row',
					padding: 10,
					background: 'lightgrey',
					borderRadius: 5,
					width: 1000,
				}}
			>
				{fields.map(field => (
					<Task key={field.field_id} field={field} />
				))}
			</div>
		</SortableContext>
	);
};

const Task = ({ field }) => {
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id: field.field_id });

	return (
		<Card
			ref={setNodeRef}
			{...attributes}
			style={{
				transition,
				transform: CSS.Transform.toString(transform),
				marginRight: 10,
				background: 'white',
				width: `${100 / field.size}%`,
				borderRadius: 5,
			}}
		>
			{field.field_id}{' '}
			<i
				className='ri-drag-move-2-line'
				style={{ cursor: 'grab', float: 'right' }}
				{...listeners}
			></i>
		</Card>
	);
};

export const ScreenLayoutConfig = ({ fields }) => {
	const [tasks, setTasks] = useState([
		[
			{
				field_id: 'Campo 1',
				size: 4,
			},
			{
				field_id: 'Campo 2',
				size: 4,
			},
			{
				field_id: 'Campo 3',
				size: 4,
			},
		],
		[
			{
				field_id: 'Campo 4',
				size: 4,
			},
			{
				field_id: 'Campo 5',
				size: 4,
			},
		],
		[
			{
				field_id: 'Campo 6',
				size: 4,
			},
		],
	]);

	const [items, setItems] = useState(
		tasks.reduce((acc, row, index) => {
			acc[index] = row.map(field => ({
				field_id: field.field_id,
				size: field.size,
			}));
			return acc;
		}, {})
	);

	const [activeId, setActiveId] = useState();

	return (
		<>
			<div>{JSON.stringify(fields)}</div>
			<DndContext
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				collisionDetection={closestCorners}
			>
				{Object.keys(items).map(rowId => (
					<Row key={rowId} rowId={rowId} fields={items[rowId]} />
				))}
				<DragOverlay>
					{activeId ? (
						<Task
							field={{
								field_id: activeId,
								size: 4,
							}}
						/>
					) : null}
				</DragOverlay>
			</DndContext>
		</>
	);

	function findContainer(id) {
		if (id in items) {
			return id;
		}

		return Object.keys(items).find(key => items[key].includes(id));
	}

	function handleDragStart(event) {
		const { active } = event;
		const { id } = active;

		setActiveId(id);
	}

	function handleDragOver(event) {
		const { active, over, draggingRect } = event;
		const { id } = active;
		const { id: overId } = over;

		// Find the containers
		const activeContainer = findContainer(id);
		const overContainer = findContainer(overId);

		// console.log({
		// 	id,
		// 	overId,
		// 	activeContainer,
		// 	overContainer,
		// });

		if (
			!activeContainer ||
			!overContainer ||
			activeContainer === overContainer
		) {
			return;
		}

		setItems(prev => {
			const activeItems = prev[activeContainer];
			const overItems = prev[overContainer];

			// Find the indexes for the items
			const activeIndex = activeItems.indexOf(id);
			const overIndex = overItems.indexOf(overId);

			let newIndex;
			if (overId in prev) {
				// We're at the root droppable of a container
				newIndex = overItems.length + 1;
			} else {
				const isBelowLastItem =
					over &&
					overIndex === overItems.length - 1 &&
					draggingRect.offsetTop > over.rect.offsetTop + over.rect.height;

				const modifier = isBelowLastItem ? 1 : 0;

				newIndex =
					overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
			}

			return {
				...prev,
				[activeContainer]: [
					...prev[activeContainer].filter(item => item !== active.id),
				],
				[overContainer]: [
					...prev[overContainer].slice(0, newIndex),
					items[activeContainer][activeIndex],
					...prev[overContainer].slice(
						newIndex,
						prev[overContainer].length
					),
				],
			};
		});
	}

	function handleDragEnd(event) {
		const { active, over } = event;
		const { id } = active;
		const { id: overId } = over;

		const activeContainer = findContainer(id);
		const overContainer = findContainer(overId);

		if (
			!activeContainer ||
			!overContainer ||
			activeContainer !== overContainer
		) {
			return;
		}

		const activeIndex = items[activeContainer].indexOf(active.id);
		const overIndex = items[overContainer].indexOf(overId);

		if (activeIndex !== overIndex) {
			setItems(items => ({
				...items,
				[overContainer]: arrayMove(
					items[overContainer],
					activeIndex,
					overIndex
				),
			}));
		}

		setActiveId(null);
	}
};
